import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/useToast';
import { UserPlus, Trash2, Shield, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '@/hooks/useUsers';
import { useWarehouses } from '@/hooks/useWarehouses';

interface UserData {
  id: string;
  username: string;
  password: string;
  role: 'admin' | 'warehouse';
  warehouseCode?: string;
  warehouseName?: string;
}

export default function Users() {
  const { t } = useLanguage();
  const { user: currentUser, isAdmin } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { users: apiUsers, createUser, deleteUser } = useUsers();
  const { warehouses: apiWarehouses } = useWarehouses();

  const users = apiUsers || [];
  const warehouses = apiWarehouses || [];
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'warehouse' as 'admin' | 'warehouse',
    warehouseCode: ''
  });

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  // Removed loadUsers and loadWarehouses - now using API hooks

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!formData.username || !formData.password) {
      toast({
        title: t('error'),
        description: 'Please fill all required fields',
        variant: 'destructive',
      });
      return;
    }

    if (formData.role === 'warehouse' && !formData.warehouseCode) {
      toast({
        title: t('error'),
        description: 'Please select a warehouse',
        variant: 'destructive',
      });
      return;
    }

    const warehouse = warehouses.find(w => w.code === formData.warehouseCode);

    createUser({
      username: formData.username,
      password: formData.password,
      role: formData.role,
      warehouse_code: formData.role === 'warehouse' ? formData.warehouseCode : null,
      warehouse_name: formData.role === 'warehouse' ? warehouse?.name : null
    });

    setFormData({
      username: '',
      password: '',
      role: 'warehouse',
      warehouseCode: ''
    });
    setIsDialogOpen(false);
  };

  const handleDelete = (userId: number) => {
    deleteUser(userId);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600">Manage system users and permissions</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New User</DialogTitle>
                <DialogDescription>
                  Create a new user account with specific permissions
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="Enter username"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={formData.role}
                      onValueChange={(value: 'admin' | 'warehouse') => setFormData({ ...formData, role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="warehouse">Warehouse User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.role === 'warehouse' && (
                    <div className="space-y-2">
                      <Label htmlFor="warehouse">Warehouse</Label>
                      <Select
                        value={formData.warehouseCode}
                        onValueChange={(value) => setFormData({ ...formData, warehouseCode: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select warehouse" />
                        </SelectTrigger>
                        <SelectContent>
                          {warehouses.map((warehouse) => (
                            <SelectItem key={warehouse.code} value={warehouse.code}>
                              {warehouse.name} ({warehouse.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button type="submit">
                    {t('save')}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <Card key={user.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{user.username}</CardTitle>
                  <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                    {user.role === 'admin' ? (
                      <><Shield className="h-3 w-3 mr-1" /> Admin</>
                    ) : (
                      <><Building2 className="h-3 w-3 mr-1" /> Warehouse</>
                    )}
                  </Badge>
                </div>
                {user.warehouseName && (
                  <CardDescription>{user.warehouseName}</CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    <div>ID: {user.id}</div>
                    {user.warehouseCode && (
                      <div>Code: {user.warehouseCode}</div>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(user.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {users.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No users yet</h3>
              <p className="text-gray-500 mb-4">
                Create user accounts for warehouse managers
              </p>
              <Button onClick={() => setIsDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Add First User
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
